import * as React from "react";
import {
    ApiQuery,
    ApiQueryExample,
    FilterMetadata,
} from "~/modules/filters/types";
import { RightDrawer } from "~/components/RightDrawer";
import { InlineFilters } from "./InlineFilters";
import { FilterEditor } from "./FilterEditor";
import { FilterControls } from "./FilterControls";
import { ExportType } from "~/modules/downloads/types";
import { areFiltersIdentical } from "../areFiltersIdentical";
import { cn } from "~/lib/utils";
import { trackCloseApiTools, trackOpenApiTools } from "./ApiDocs/analytics";
import { sanitizeFilter } from "./functions/sanitizeFilter";
import { ModeChangeConfirm } from "./ModeChangeConfirm";
import { ApiDocs } from "./ApiDocs";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

// TODO - rename this function
// TODO - cleanup the props here, and in downstream components
export function FiltersLayout(props: {
    query: ApiQuery;
    filterMetadata: FilterMetadata[];
    queryExamples: ApiQueryExample[];
    enableProSearch?: boolean;
    enableBasicFilters?: boolean;
    enableClearFilters?: boolean;
    onChange: (updatedApiQuery: ApiQuery) => void;
    internalQuery: {
        value: ApiQuery;
        loading: boolean;
        resultCount: number;
        onChange: (internalQuery: ApiQuery) => void;
    };
    appliedQuery: {
        loading: boolean;
    };
    onExport?: (exportType: ExportType) => void;
    // TODO - pass in API examples here - or pass in broader API meta data?
    status?: {
        loading: boolean;
        resultCount: number;
    };
    onResetQuery: () => void;
    apiDocsBaseUrl?: string;
    sharing?: {
        enabled: boolean;
        url: string;
    };
    title?: string;
}) {
    const {
        query,
        filterMetadata,
        queryExamples,
        onChange,
        onExport,
        enableProSearch = true,
        enableBasicFilters = false,
        enableClearFilters = true,
        status,
        onResetQuery, // TODO - reimplement this....?
        apiDocsBaseUrl,
        title,
        sharing = {
            enabled: false,
            url: "",
        },
    } = props;

    const [showApiDocs, setShowApiDocs] = React.useState(false);
    // const [isProSearch, setIsProSearch] = React.useState(true); // Enable pro-search
    const [isProSearch, setIsProSearch] = React.useState(!enableBasicFilters); // Enable pro-search
    const [showingModeChangeModal, showModeChangeModal] = React.useState(false);
    const { hasApiAccess } = useUser();

    // TODO - add support for "internalQuery" here
    // TODO - update internalquery when props.query changes w/ useEffect hook
    // const [internalQuery, setInternalQuery] = React.useState(query);
    const internalQuery = props.internalQuery.value;
    const setInternalQuery = props.internalQuery.onChange;

    // const [lastChangedAt, setLastChangedAt] = React.useState(Date.now());
    const [hasUnappliedEdits, setHasUnappliedEdits] = React.useState(false);

    // Invoke props.internalQuery.onChange to update latest state
    React.useEffect(() => {
        // setLastChangedAt(Date.now());
        // TODO - would be good to configure this so we can check equality against the current filters
        // If the applied and internal queries are congruent, we should set hasUnappliedEdits to "false"
        const noChangesFound = areFiltersIdentical({
            currentFilter: query.filter,
            latestFilter: internalQuery.filter,
        });

        if (noChangesFound) {
            setHasUnappliedEdits(false);
        } else {
            props.internalQuery.onChange(internalQuery);
            setHasUnappliedEdits(true);
        }
    }, [internalQuery]);

    // ONLY RENDER FILTER CONTROLS WHEN REQUIREMENTS ARE MET
    // OTHEWISE, SKIP RENDERING.
    // TODO - this should be handled differently, either as a composable component OR controlled via props
    const showControls = enableProSearch || apiDocsBaseUrl || onExport;

    // Define a flag if filterMetadata is empty
    const showFilters = filterMetadata.length > 0;

    return (
        <div
            className={cn("flex flex-col w-full", {
                "mb-4": !showFilters,
            })}
        >
            {showControls && (
                <FilterControls
                    title={title}
                    appliedQuery={query}
                    hasChanges={hasUnappliedEdits}
                    clearChanges={() => {
                        setInternalQuery(props.query);
                    }}
                    isProSearch={isProSearch}
                    sharing={sharing}
                    enableProSearch={enableProSearch}
                    enableBasicFilters={enableBasicFilters}
                    // // // //
                    onChangeMode={() => {
                        // Show confirmation here to inform the user that switching from pro to basic will reset the applied filters.
                        // but ONLY IF THERE ARE FILTERS APPLIED
                        // i.e. sanitize props.query and check if filter is empty
                        // if it is empty, just change
                        // if not, show modal and confirm change

                        // QUESTION - should this be the applied query or the internal query?
                        const sanitizedFilter = sanitizeFilter(query.filter);

                        // If there are no internally applied filters,
                        // flush internal state and change between filter/prosearch
                        if (sanitizedFilter === undefined) {
                            setHasUnappliedEdits(false);
                            setInternalQuery({
                                ...internalQuery,
                                filter: undefined,
                            });
                            setIsProSearch(!isProSearch);
                            return;
                        }

                        // If some filter changes will be lost, show confirmation modal
                        showModeChangeModal(true);
                        return;
                    }}
                    status={status}
                    onExport={onExport}
                    onClickApi={
                        apiDocsBaseUrl === undefined
                            ? undefined
                            : () => {
                                  trackOpenApiTools({ apiDocsBaseUrl });
                                  setShowApiDocs(true);
                              }
                    }
                    queryExamples={queryExamples}
                    onSelectQueryExample={(
                        selectedExample: ApiQueryExample,
                    ) => {
                        // Selecting an example applies it immediately - update
                        // the applied query alongside the internal one so the
                        // results refresh without a separate "Apply Search"
                        setHasUnappliedEdits(false);
                        setInternalQuery(selectedExample.query);
                        onChange(selectedExample.query);
                    }}
                    applyQuery={() => {
                        setHasUnappliedEdits(false);
                        onChange(internalQuery);
                    }}
                    // TODO - rename this to internal query state
                    // TODO - refine this whole goddamn implementation
                    internalQuery={{
                        loading: props.internalQuery.loading,
                        resultCount: props.internalQuery.resultCount,
                    }}
                />
            )}

            {/* Only render filters if filterMetadata is present */}
            {showFilters && enableClearFilters && (
                <div
                    className={cn(
                        "flex items-center gap-4 w-full justify-between",
                        {
                            "py-4": showControls,
                            "pb-4": !showControls,
                        },
                    )}
                >
                    {!isProSearch && (
                        <InlineFilters
                            query={query}
                            onChange={(updatedApiQuery) => {
                                onChange(updatedApiQuery);
                            }}
                            filterMetadata={filterMetadata}
                            enableClearFilters={enableClearFilters}
                            loading={props.appliedQuery.loading}
                        />
                    )}

                    {isProSearch && (
                        <FilterEditor
                            query={internalQuery}
                            onChange={setInternalQuery}
                            filterMetadata={filterMetadata}
                        />
                    )}
                </div>
            )}

            {/* Only show ApiDocs when apiDocsBaseUrl is defined */}
            {apiDocsBaseUrl && (
                <RightDrawer
                    size="xl"
                    hideHeader
                    open={showApiDocs}
                    onClose={() => {
                        trackCloseApiTools({ apiDocsBaseUrl });
                        setShowApiDocs(false);
                    }}
                >
                    <ApiDocs
                        query={query}
                        baseUrl={apiDocsBaseUrl}
                        hasApiAccess={hasApiAccess}
                    />
                </RightDrawer>
            )}

            <ModeChangeConfirm
                show={showingModeChangeModal}
                onDismiss={() => {
                    showModeChangeModal(false);
                }}
                onConfirm={() => {
                    // on-change-mode callback logic here
                    setHasUnappliedEdits(false);
                    onChange({
                        ...internalQuery,
                        filter: undefined,
                    });
                    setInternalQuery({
                        ...internalQuery,
                        filter: undefined,
                    });
                    setIsProSearch(!isProSearch);
                    showModeChangeModal(false);
                }}
            />
        </div>
    );
}
